import { useImperativeHandle, useMemo, useRef } from 'react';
import { cx } from 'class-variance-authority';
import { useForm, useSelector } from '@tanstack/react-form';

import { Button, InputField, SelectField } from '@nvision/ui';

import { type UpdateFieldOptions, updateFieldDependencies } from '@/utils/form';

import { useCountries, usePortTypes, useStates } from '../../../hooks';
import { type PortSearchFormValues, portSearchFormSchema } from '../../../models';

import styles from './port-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface PortSearchFormProps {
  defaultValues?: Partial<PortSearchFormValues>;
  formRef: React.RefObject<SearchFormRef>;
  handleSubmit: (values: PortSearchFormValues) => void;
  handleReset: () => void;
}

const getDefaultSearchFormValues = (): PortSearchFormValues => ({
  country: undefined,
  portCity: undefined,
  portCode: undefined,
  portName: undefined,
  portPostal: undefined,
  portState: null,
  portType: null,
});

// When a parent field changes, its children (and their children) are reset.
// e.g. country → port state
const FIELD_DEPENDENCIES: Partial<Record<keyof PortSearchFormValues, Array<keyof PortSearchFormValues>>> = {
  country: ['portState'],
  portType: ['portState'],
};

export const updatePortSearchFormValues = (
  prevFilters: Partial<PortSearchFormValues>,
  nextFilters: Partial<PortSearchFormValues>,
  options?: UpdateFieldOptions<PortSearchFormValues>,
): Partial<PortSearchFormValues> => {
  return updateFieldDependencies(prevFilters, nextFilters, FIELD_DEPENDENCIES, options);
};

export const PortSearchForm: React.FC<PortSearchFormProps> = ({
  defaultValues,
  formRef,
  handleSubmit,
  handleReset,
}) => {
  const portDefaultValues = useMemo(() => {
    return {
      ...getDefaultSearchFormValues(),
      ...defaultValues,
    };
  }, [defaultValues]);

  const prevFilters = useRef(portDefaultValues);

  const form = useForm({
    defaultValues: portDefaultValues,
    listeners: {
      onChange: ({ formApi }) => {
        const curr = formApi.state.values;
        const prev = prevFilters.current;

        if (curr === prev) {
          return;
        }

        prevFilters.current = curr;

        updatePortSearchFormValues(prev, curr, {
          onChange: formApi.setFieldValue,
        });
      },
    },
    onSubmit: async (props) => {
      handleSubmit(props.value);
    },
    validators: {
      onChange: portSearchFormSchema,
      onSubmit: portSearchFormSchema,
    },
  });

  const resetSearchForm = () => {
    form.reset();
    handleReset();
    prevFilters.current = portDefaultValues;
  };
  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: resetSearchForm,
    submit: () => {
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const { data: countries, isLoading: countriesLoading } = useCountries();
  const country = useSelector(form.store, (store) => store.values.country);
  const { data: states, isLoading: statesLoading } = useStates({ CountryCode: String(country?.id) ?? '' });
  const { data: portTypes, isLoading: portTypesLoading } = usePortTypes();

  return (
    <form onSubmit={onSubmit} className={styles.form}>
      <div className={styles.filters}>
        <form.Field name="country">
          {(field) => (
            <SelectField
              className={cx(styles.field, styles.country)}
              value={field.state.value}
              label="Country"
              options={countries?.list || []}
              placeholder="Select"
              size="sm"
              isRequired
              clearable
              onBlur={field.handleBlur}
              onSelectionChange={(country) => field.handleChange(country ?? undefined)}
              isLoading={countriesLoading}
              errorMessage={field.state.meta.errors?.[0]?.message}
            />
          )}
        </form.Field>
        <form.Field name="portType">
          {(field) => (
            <SelectField
              className={styles.field}
              value={field.state.value}
              label="Port Type"
              options={portTypes.list}
              placeholder="Select"
              size="sm"
              clearable
              onBlur={field.handleBlur}
              onSelectionChange={(portType) => field.handleChange(portType)}
              isLoading={portTypesLoading}
            />
          )}
        </form.Field>
        <form.Field name="portName">
          {(field) => (
            <InputField
              className={cx(styles.field, styles.portName)}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Port Name"
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="portCode">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Port Code"
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="portPostal">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Port Postal"
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="portCity">
          {(field) => (
            <InputField
              className={cx(styles.field, styles.portCity)}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Port City"
              placeholder="Enter"
              size="sm"
            />
          )}
        </form.Field>
        <form.Field name="portState">
          {(field) => (
            <SelectField
              className={cx(styles.field, styles.portState)}
              value={field.state.value ?? null}
              label="Port State"
              options={states?.list || []}
              placeholder="Start typing..."
              size="sm"
              clearable
              onBlur={field.handleBlur}
              onSelectionChange={(portState) => field.handleChange(portState)}
              isLoading={statesLoading}
              isDisabled={!country}
            />
          )}
        </form.Field>
      </div>

      <div className={styles.actions}>
        <Button type="submit">Search</Button>
        <Button type="button" outline secondary onClick={() => resetSearchForm()}>
          Reset
        </Button>
      </div>
    </form>
  );
};
